import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatedGridPattern } from '../../ui/animated-grid-pattern';
import { cn } from "../../../lib/utils";
import './IntroSectionFormationEntra.css';
import { FeaturesSectionWithHoverEffects } from '../../ui/feature-section-with-hoover-effect';
import CloudArchitectureImg from '../../../assets/images/logo-landing-itmonkeys-grey.png';
import formationEntraFeatures from '../../../data/features/formationEntra';
import { TextShimmer } from '../../ui/text-shimmer';
import { TextGradientScroll } from '../../ui/text-gradient-scroll';

const IntroSectionFormationEntra: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="intro-section-home intro-section-formation-entra">
      <div className="container-max-width tw-relative tw-flex tw-h-[500px] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-border tw-bg-background tw-md:pt-20 tw-md:shadow-xl">
        <div className="centered-title-top">
          <h2>{t('formationEntra.intro.title', 'Administration cloud et gestion des identités')}</h2>
          <div style={{
            margin: '0 0 10px 0',
            marginTop: '10px',
            paddingTop: '0',
            overflowWrap: 'break-word',
            maxWidth: '940px',
            textAlign: 'left',
            fontWeight: '500',
            lineHeight: '1.68',
            fontSize: '30px'
          }}>
            <TextShimmer
              as="h3"
              duration={3}
              className="text-shimmer-clean [--base-color:rgba(54,52,52,1)] [--base-gradient-color:#ffffff]"
            >
              {t('formationEntra.intro.description', 'En tant que Responsable IT, DSI, RSSI ou consultant en sécurité, renforcez la posture de sécurité de votre organisation dans le cloud. Cette formation de 22 heures vous apprendra à gérer et optimiser votre environnement cloud avec des outils et méthodes de pointe.')}
            </TextShimmer>
          </div>
          <div className="title-image-container">
            <img src={CloudArchitectureImg} alt={t('formationEntra.intro.imgAlt', 'Architecture Cloud')} className="title-below-image overflow-visible" />
          </div>
        </div>
        <AnimatedGridPattern
          numSquares={30}
          maxOpacity={0.1}
          duration={3}
          repeatDelay={1}
          className={cn(
            "[mask-image:radial-gradient(500px_circle_at_center,white,transparent)]",
            "tw-inset-x-0 tw-inset-y-[-30%] tw-h-[200%] tw-skew-y-12",
          )}
        />
      </div>

      <div className="tw-w-full tw-flex tw-flex-col tw-mt-8 tw-mb-[2em]">
        <div className="tw-w-full tw-mt-[11em]">
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[42%_1fr] tw-gap-8 tw-items-center">
            {/* Gradient Column */}
            <div className="intro-gradient-column tw-relative tw-h-[31em] tw-flex tw-justify-center lg:tw-justify-start" style={{ borderTopLeftRadius: '100px' }}>
              <div
                aria-hidden="true"
                className="tw-absolute tw-inset-0 tw-pointer-events-none tw-z-0"
                style={{ borderTopLeftRadius: '100px' }}
              >
                <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-[#24bbd9]/20 tw-via-[#4166bc]/10 tw-to-transparent" style={{ borderTopLeftRadius: '100px' }} />
                <div className="tw-absolute -tw-top-10 -tw-left-10 tw-h-64 tw-w-64 tw-rounded-full tw-bg-[#24bbd9]/20 tw-blur-3xl tw-opacity-60" style={{ borderTopLeftRadius: '100px' }} />
              </div>
            </div>

            {/* Features Column */}
            <div>
              <div className="intro-text-2" style={{ maxWidth: '100%' }}>
                <h2>Public cible</h2>
                <h3 className="public-cible-description">Cette formation s'adresse aux professionnels IT souhaitant se spécialiser dans la gestion des identités Azure.</h3>
              </div>
              <FeaturesSectionWithHoverEffects
                limit={4}
                displayInRow={true}
                featuresData={formationEntraFeatures}
              />
            </div>
          </div>
        </div>
      </div>

    <div className="intro-section intro-challenges-section tw-w-full tw-relative tw-flex tw-items-center tw-pt-[1em] tw-overflow-hidden" style={{ borderBottomRightRadius: '100px' }}>
      <div
        aria-hidden="true"
        className="tw-absolute tw-inset-y-0 tw-left-1/2 tw-w-[50vw] tw-pointer-events-none tw-z-0"
      >
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-tl tw-from-[#24bbd9]/20 tw-via-[#4166bc]/10 tw-to-transparent" />
        <div className="tw-absolute -tw-bottom-10 -tw-right-10 tw-h-64 tw-w-64 tw-rounded-full tw-bg-[#24bbd9]/20 tw-blur-3xl tw-opacity-60" />
      </div>

      <div className="intro-row-1 tw-relative tw-overflow-hidden tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-items-center">

        <div className="intro-text-1 intro-text-no-padding-top tw-relative tw-z-10 tw-pl-[70px]">
          <h2 style={{ marginBottom: '42px', maxWidth: '500px', fontSize: '62px' }}>
            {t('formationEntra.problems.title', 'Les défis de la gestion des identités')}
          </h2>
          <div className="tw-text-2xl md:tw-text-3xl tw-space-y-6">
          <div className="tw-block tw-pt-4">
            <TextGradientScroll text={t('formationEntra.problems.identity', 'Gestion complexe des identités hybrides')} />
          </div>
          <div className="tw-block tw-pt-4">
            <TextGradientScroll text={t('formationEntra.problems.access', 'Contrôle d\'accès insuffisant')} />
          </div>
          <div className="tw-block tw-pt-4">
            <TextGradientScroll text={t('formationEntra.problems.compliance', 'Non-conformité aux régulations')} />
          </div>
          <div className="tw-block tw-pt-4">
            <TextGradientScroll text={t('formationEntra.problems.breaches', 'Risques de violations de données')} />
          </div>
          <div className="tw-block tw-pt-4">
            <TextGradientScroll text={t('formationEntra.problems.governance', 'Gouvernance des identités défaillante')} />
          </div>
          <div className="tw-block tw-pt-4">
            <TextGradientScroll text={t('formationEntra.problems.monitoring', 'Surveillance des accès limitée')} />
          </div>
          <div className="tw-block tw-pt-4">
            <TextGradientScroll text={t('formationEntra.problems.integration', 'Intégration d\'applications difficile')} />
          </div>
          </div>
        </div>

        {/* Right column image */}
        <div className="tw-hidden lg:tw-flex tw-justify-center tw-items-center tw-relative tw-z-10">
        </div>
      </div>
    </div>

    </div>
  );
};

export default IntroSectionFormationEntra;
